import { useEffect, useState } from "react";
import "../../styles/dashboard.css";
import { fetchDashboardStats } from "../../api/dashboard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* ------------------ SKELETONS ------------------ */

function KpiSkeleton() {
  return (
    <div className="kpi-card animate-pulse dark:bg-gray-900 dark:border-gray-800">
      <div className="kpi-icon-container kpi-icon-neutral dark:bg-gray-800" />
      <div className="kpi-text-group space-y-2">
        <div className="h-3 w-28 bg-secondary/20 dark:bg-white/10 rounded" />
        <div className="h-5 w-16 bg-secondary/30 dark:bg-white/15 rounded" />
      </div>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="dashboard-card animate-pulse p-6 dark:bg-gray-900 dark:border-gray-800">
      <div className="h-4 w-40 bg-secondary/20 dark:bg-white/10 rounded mb-4" />
      <div className="h-[280px] bg-secondary/10 dark:bg-white/5 rounded-lg" />
    </div>
  );
}

/* ------------------ DASHBOARD ------------------ */

const pieColors = ["#02007B", "#F3B14E", "#B1CB40", "#E63946"];

export default function Dashboard() {
  const [filter, setFilter] = useState("day");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await fetchDashboardStats();
        setStats(data);
      } catch (e) {
        console.error("Failed to load dashboard stats", e);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const data = stats?.trends?.[filter] ?? [];
  const totals = stats?.totals ?? {
    active: 0,
    pending: 0,
    technicians: 0,
    dueToday: 0,
  };

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const axisTickColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#374151" : "#e5e7eb";
  const tooltipBg = isDark ? "#111827" : "#ffffff";
  const tooltipBorder = isDark ? "#374151" : "#e5e7eb";
  const tooltipText = isDark ? "#f3f4f6" : "#111827";

  return (
    <div className="dashboard-content space-y-3 text-gray-900 dark:text-gray-100">
      {/* ================= SUMMARY ================= */}
      <section className="dashboard-section">
        <div className="dashboard-grid dashboard-grid-4 gap-2">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)
          ) : (
            <>
              {[
                { label: "Active Tasks", value: totals.active, icon: "📘" },
                {
                  label: "Pending Acceptance",
                  value: totals.pending,
                  icon: "⏳",
                },
                {
                  label: "Available Technicians",
                  value: totals.technicians,
                  icon: "🧑🏾‍🔧",
                },
                { label: "Due Today", value: totals.dueToday, icon: "📅" },
              ].map((kpi) => (
                <div
                  key={kpi.label}
                  className="kpi-card flex items-center gap-4 px-5 py-4 dark:bg-gray-900 dark:border-gray-800"
                >
                  <div className="kpi-icon-container kpi-icon-neutral shrink-0 dark:bg-gray-800">
                    <span className="kpi-icon">{kpi.icon}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide font-semibold text-secondary/60 dark:text-gray-400">
                      {kpi.label}
                    </span>
                    <span className="text-xl font-bold text-secondary mt-0.5 dark:text-white">
                      {kpi.value}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {/* ================= TRENDS ================= */}
      <section className="dashboard-section">
        <div className="flex items-center justify-between mb-4 px-4 py-3 bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex gap-2">
            {["day", "month", "year"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-sm border transition ${
                  filter === f
                    ? "border-secondary text-secondary font-semibold bg-white dark:bg-gray-800 dark:text-white dark:border-gray-700"
                    : "border-secondary/20 text-secondary/60 hover:border-secondary/40 bg-transparent dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-gray-800/60"
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-grid dashboard-grid-2 gap-6">
          {loading ? (
            <>
              <ChartSkeleton />
              <ChartSkeleton />
            </>
          ) : (
            <>
              {/* LINE */}
              <div className="dashboard-card p-6 dark:bg-gray-900 dark:border-gray-800">
                <h2 className="text-sm font-semibold text-secondary dark:text-white mb-4">
                  Task Trends ({filter})
                </h2>

                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: axisTickColor }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: axisTickColor }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: tooltipBg,
                        borderRadius: "8px",
                        border: `1px solid ${tooltipBorder}`,
                        fontSize: "12px",
                        color: tooltipText,
                      }}
                      labelStyle={{ color: tooltipText }}
                    />
                    <Legend wrapperStyle={{ fontSize: "12px", color: axisTickColor }} />
                    <Line
                      dataKey="active"
                      stroke="#02007B"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    <Line
                      dataKey="pending"
                      stroke="#F3B14E"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    <Line
                      dataKey="due"
                      stroke="#B1CB40"
                      strokeWidth={2.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* BAR */}
              <div className="dashboard-card p-6 dark:bg-gray-900 dark:border-gray-800">
                <h2 className="text-sm font-semibold text-secondary dark:text-white mb-4">
                  Technician vs Active Tasks
                </h2>

                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: axisTickColor }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: axisTickColor }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: tooltipBg,
                        borderRadius: "8px",
                        border: `1px solid ${tooltipBorder}`,
                        fontSize: "12px",
                        color: tooltipText,
                      }}
                      labelStyle={{ color: tooltipText }}
                    />
                    <Legend wrapperStyle={{ fontSize: "12px", color: axisTickColor }} />
                    <Bar
                      dataKey="technicians"
                      fill="#B1CB40"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="active"
                      fill="#F3B14E"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ================= BREAKDOWN ================= */}
      <section className="dashboard-section">
        <div className="dashboard-card p-6 dark:bg-gray-900 dark:border-gray-800">
          {loading ? (
            <ChartSkeleton />
          ) : (
            <>
              <h2 className="text-sm font-semibold text-secondary dark:text-white mb-4">
                Task Breakdown
              </h2>

              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Active", value: totals.active },
                      { name: "Pending", value: totals.pending },
                      { name: "Technicians", value: totals.technicians },
                      { name: "Due Today", value: totals.dueToday },
                    ]}
                    dataKey="value"
                    outerRadius={110}
                    innerRadius={55}
                    paddingAngle={2}
                  >
                    {pieColors.map((color, index) => (
                      <Cell key={index} fill={color} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: "12px", color: axisTickColor }} />
                </PieChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </section>
    </div>
  );
}